import React from "react";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  createHttpLink,
} from "@apollo/client";
import { RetryLink } from "@apollo/client/link/retry";
import { setContext } from "@apollo/client/link/context";
import { from } from "@apollo/client";

import { clearStorage } from "utility/Function";
import { setAnalyticsUserId } from "utility/FirebaseAnalytics";
import {
  setUserId as setMixpanelUserId,
  getDeviceId,
  getUtmParameters,
} from "utility/mixpanel";
import { getAccessToken, refreshAccessToken } from "providers/auth-tokens";
import { createAuthErrorLink } from "providers/auth-error-link";

import constants from "Constant";

// Create a custom link that adds UTM parameters to the URI
const utmLink = (operation, forward) => {
  const utmParams = getUtmParameters();
  if (utmParams) {
    const uri = operation.getContext().uri || constants.API_URL;
    const url = new URL(uri);

    // Add UTM parameters to the URL
    Object.keys(utmParams).forEach((key) => {
      url.searchParams.append(key, utmParams[key]);
    });

    // Update the operation context with the new URI
    operation.setContext({
      uri: url.toString(),
    });
  }
  return forward(operation);
};

const httpLink = createHttpLink({
  uri: constants.API_URL,
  fetchOptions: {
    keepalive: true,
  },
});

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error, _operation) => !!error,
  },
});

export const authenticationFailed = () => {
  alert("Your token has expired. Please log in again.");
  window.history.go(0);
  clearStorage();
  setAnalyticsUserId(null);
  setMixpanelUserId(null);
};

/**
 * Refresh the in-memory access token via the cookie-bearing
 * /api/v1/auth/refresh endpoint. The legacy graphql-jwt fallback retired
 * with the HS256 cutover — anyone without an OAuth refresh cookie has
 * to sign in via the unified flow.
 */
const refreshToken = async () => {
  const newAccessToken = await refreshAccessToken();
  if (!newAccessToken) {
    authenticationFailed();
  }
};

// Recover an expired access token by rotating it from the refresh cookie
// and replaying the operation. Triggers on a transport 401 (REST
// surfaces) and on the GraphQL-200-with-auth-error shape Platform
// returns for an expired bearer (WONE-983).
const errorControl = createAuthErrorLink({
  refresh: refreshToken,
  onDisabledUser: authenticationFailed,
});

const authLink = setContext(async (_, { headers }) => {
  const oauthToken = getAccessToken();
  const mixpanelDeviceId = getDeviceId();

  return {
    headers: {
      ...headers,
      authorization: oauthToken ? `Bearer ${oauthToken}` : "",
      "X-MIXPANEL-DEVICE-ID": mixpanelDeviceId,
    },
  };
});

const readPaginatedArray = (existing, { args }) => {
  const page = existing && existing.slice(args.offset);
  if (page && page.length > 0) {
    return page;
  }
};

export const typePolicies = {
  SelectInputOption: {
    keyFields: false,
  },
  // Score ids name the score definition, not the reading, so the current
  // score and the same score inside a history snapshot share an id.
  // Normalised, whichever arrives last overwrites the other's fields (e.g.
  // scoreDifference). Mobile's cache embeds them the same way. A snapshot
  // itself is keyed by its response id, unique per reading, so it stays
  // normalised.
  OverallIndexScore: {
    keyFields: false,
  },
  DomainScore: {
    keyFields: false,
  },
  // No id, so it lives inside its Learner. Queries select different
  // fields of it, and merging keeps one's fields when another writes.
  AssessmentSchedule: {
    merge: true,
  },
  MultiSelectInput: {
    keyFields: ["id"],
    fields: {
      options: {
        merge: false,
      },
    },
  },
  Learner: {
    keyFields: ["id"],
    fields: {
      screens: {
        merge(existing, incoming) {
          return { ...existing, ...incoming };
        },
      },
    },
  },
  LearnerScreens: {
    fields: {
      index: {
        merge(existing, incoming) {
          return { ...existing, ...incoming };
        },
      },
    },
  },
  LearnerMixedContentList: {
    fields: {
      items: {
        merge(existing = [], incoming) {
          const merged = existing.slice(0);
          const end = incoming.length;
          for (let i = 0; i < end; ++i) {
            merged[i] = incoming[i];
          }

          return merged;
        },
      },
    },
  },
  LearnerIndexScreen: {
    fields: {
      breakdown: {
        merge(existing, incoming) {
          return { ...existing, ...incoming };
        },
      },
    },
  },
  LearnerIndexBreakdown: {
    fields: {
      energy: {
        merge(existing, incoming) {
          return { ...existing, ...incoming };
        },
      },
      resilience: {
        merge(existing, incoming) {
          return { ...existing, ...incoming };
        },
      },
      activity: {
        merge(existing, incoming) {
          return { ...existing, ...incoming };
        },
      },
    },
  },
  LearnerIndexBreakdownPillar: {
    fields: {
      score: {
        merge(existing, incoming) {
          return { ...existing, ...incoming };
        },
      },
    },
  },
  LearnerIndexScore: {
    merge: true,
  },
  LearnerIndexCoach: {
    merge: true,
  },

  LearnerIndexScoreGraph: {
    merge: true,
  },

  LearnerIndexScoreDataPeriods: {
    merge: true,
    fields: {
      week: {
        merge(existing, incoming, { args }) {
          const merged = existing ? existing.slice(0) : [];
          const end = args.offset + Math.min(incoming.length);
          for (let i = args.offset; i < end; ++i) {
            merged[i] = incoming[i - args.offset];
          }
          return merged;
        },
        read: readPaginatedArray,
      },
      month: {
        merge(existing, incoming, { args }) {
          const merged = existing ? existing.slice(0) : [];
          const end = args.offset + Math.min(incoming.length);
          for (let i = args.offset; i < end; ++i) {
            merged[i] = incoming[i - args.offset];
          }
          return merged;
        },
        read: readPaginatedArray,
      },
      year: {
        merge(existing, incoming, { args }) {
          const merged = existing ? existing.slice(0) : [];
          const end = args.offset + Math.min(incoming.length);
          for (let i = args.offset; i < end; ++i) {
            merged[i] = incoming[i - args.offset];
          }
          return merged;
        },
        read: readPaginatedArray,
      },
    },
  },
  LearnerIndexScoreDataPeriod: {
    merge: true,
  },
};

const client = new ApolloClient({
  link: from([
    errorControl,
    retryLink,
    authLink,
    {
      request: utmLink,
    },
    httpLink,
  ]),
  cache: new InMemoryCache({ typePolicies }),
});

export default function ApolloClientProvider({ children }) {
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
